/**
 * THE GA4 PROPERTY, AND THE PRIVACY NOTICE THE CONSENT REQUEST DEPENDS ON.
 *
 * Both are configuration rather than behaviour, and both are validated here so
 * that a build which cannot be honest about them refuses to run rather than
 * shipping a half-answer.
 *
 * THE MEASUREMENT ID IS ENVIRONMENT-ONLY. THERE IS NO SOURCE FALLBACK.
 *
 * A previous version of this file carried the production id as a committed
 * constant with the environment value as an override. That was wrong in two
 * ways the Director named, and both are worth recording rather than quietly
 * reverting:
 *
 *   1. It put the production property's address into EVERY bundle — dev, QA,
 *      staging, preview, localhost and every disabled build — for a value that
 *      belongs to one environment.
 *   2. It made the id impossible to fake. A committed constant validated
 *      against itself meant an armed test build could not use a fake id
 *      without rewriting source or rewriting the response body, so the only
 *      harness that can exercise the route gate had to reach outside the
 *      repository to run at all.
 *
 * Absent means off. The exact production value is pinned where configuration
 * is validated — `tests/unit/book-analytics-isolation.test.js` — and not in
 * anything that ships. This module validates only SHAPE and SEPARATION, which
 * is what a runtime can honestly check.
 *
 * The production value is not configured in this workspace, because its tooling
 * blocks access to environment files. That is an activation dependency, not a
 * thing to route around: see docs/analytics/book-consent-ui.md §1.
 */

/**
 * The one hostname this property may ever run on, compared exactly.
 *
 * Not a suffix test. `book.uselura.com` is a suffix of
 * `book.uselura.com.evil.example`, and `endsWith` would hand a hostile host the
 * production stream — the same class of bug the egress allowlist was corrected
 * for when a bare `firebaseappcheck` matched as a substring.
 *
 * Not `config.webHost` either, deliberately: that is a BUILD-TIME value, so a
 * production bundle served from anywhere at all would satisfy it. This has to
 * be what the browser is actually showing.
 */
export const ELIGIBLE_HOSTNAME = "book.uselura.com";

/** GA4 measurement ids. Shape only — the exact value is configuration. */
const MEASUREMENT_ID_SHAPE = /^G-[A-Z0-9]{6,12}$/;

/**
 * PROPERTY AND STREAM NUMBERS ARE NOT IN THIS FILE, ON PURPOSE.
 *
 * They are operator identifiers for GA Admin, they are never event data, and a
 * constant nobody reads is a constant somebody eventually sends. They live in
 * `docs/analytics/book-ga4-foundation.md` §7, where the person configuring GA
 * Admin will look for them.
 */

export type PropertyFailure =
  | "no-measurement-id"
  | "malformed-measurement-id"
  | "measurement-id-is-firebase";

export type ResolvedProperty =
  | { readonly ok: true; readonly measurementId: string }
  | { readonly ok: false; readonly failures: readonly PropertyFailure[] };

/**
 * The measurement id this build was configured with, if any.
 *
 * No default. An unconfigured build has no property, which is the committed
 * state of all four environments and is why nothing can collect from this
 * repository as it stands.
 */
export function resolveMeasurementId(): ResolvedProperty {
  const env = import.meta.env;
  const id = env.VITE_BOOK_GA4_MEASUREMENT_ID;
  const failures: PropertyFailure[] = [];

  if (!id) {
    failures.push("no-measurement-id");
  } else if (!MEASUREMENT_ID_SHAPE.test(id)) {
    failures.push("malformed-measurement-id");
  }

  const firebaseId = env.VITE_FIREBASE_MEASUREMENT_ID;
  if (id && firebaseId && id === firebaseId) {
    // Two properties measuring one surface is not a configuration mistake that
    // shows up as an error; it shows up as data in a property nobody agreed to
    // send it to. Refuse rather than pick one.
    failures.push("measurement-id-is-firebase");
  }

  if (failures.length > 0) return { ok: false, failures };
  return { ok: true, measurementId: id as string };
}

/**
 * THE PRIVACY NOTICE URL, AND WHY ITS ABSENCE IS FATAL TO AN ARMED BUILD.
 *
 * The consent request is a request. "With your permission, Lura uses optional
 * analytics cookies…" alongside a link that goes nowhere is not a request — it
 * is a checkbox with a decoration, and the person answering it cannot find out
 * what they are answering. So:
 *
 *   - a build with the enable flag ON and no valid URL THROWS at boot, the
 *     same way `config/env.ts` throws on a missing API host. A deploy that
 *     could collect without a published notice must not start;
 *   - a build with the flag off and no URL is the committed state and is fine,
 *     but `privacyNoticeUrl()` returns null and BOTH analytics and the
 *     visitor-facing consent request stay off. Nothing asks a question it
 *     cannot answer, and nothing renders a dead link.
 *
 * Legal/Privacy owns the value. Nothing here invents one, and there is no
 * temporary route: an invented URL is worse than an absent one, because an
 * absent one is visibly unfinished.
 */
function readPrivacyNotice(): string | null {
  const raw = import.meta.env.VITE_BOOK_PRIVACY_NOTICE_URL;
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  // https only, and no credentials, query or fragment: this is a published
  // document, and a link carrying a query is a link carrying something.
  if (parsed.protocol !== "https:") return null;
  if (parsed.username || parsed.password) return null;
  if (parsed.search || parsed.hash) return null;
  return parsed.toString();
}

if (import.meta.env.VITE_BOOK_GA4_ENABLED === "true" && readPrivacyNotice() === null) {
  throw new Error(
    "VITE_BOOK_GA4_ENABLED is true but VITE_BOOK_PRIVACY_NOTICE_URL is missing or not a plain " +
      "https URL. Analytics may not be enabled without a published privacy notice for the " +
      "consent request to link to. Set it, or turn the flag off."
  );
}

/**
 * The published notice, or null while Legal/Privacy has not supplied one.
 *
 * Re-read on every call rather than captured at module load. The boot check
 * above is what makes an armed build fail fast; this is a plain accessor, and
 * a captured constant would make the value untestable without reloading the
 * module — the kind of seam that ends up only production takes.
 */
export function privacyNoticeUrl(): string | null {
  return readPrivacyNotice();
}
